import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProfile } from '@/contexts/profile-context';
import { logger } from '@/lib/frontend-logger';
import { EmailInput } from '@/components/redacted-email';
import { maskEmail } from '@/lib/mask-email';
import { FieldError } from '@/components/form/field-error';
import { emailSchema } from '@/lib/form/schemas';
import {
  usePatchProfileMutation,
  useResendProfileVerificationMutation,
  useVerifyProfileEmailMutation,
  type EditProfileValues,
} from '@/components/edit-profile-dialog-queries';
import { AlertPhoneDialog } from '@/components/alert-phone-dialog';
import { useAlertPhone } from '@/pages/launchpad/use-alert-phone';
import { RemoveAlertPhoneButton } from '@/components/remove-alert-phone-button';
import { TwoFactorSetting } from '@/components/two-factor-setting';

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Fixed copy (FIXED per 114-02-PLAN.md <interfaces>):
// Phase 1 title: 'Edit profile'
// Phase 2 title: 'Verify your new email'
// Inline errors (non-toast): 'Invalid or expired code' | 'That email is already in use.'
// Toasts: 'Profile updated' | 'Email verified' | 'Code resent'
const TITLE_FORM = 'Edit profile';
const TITLE_VERIFY = 'Verify your new email';

type ServerFieldError = { field: 'email' | 'code'; message: string };

const firstNameSchema = z.string().trim().min(1, 'First name is required.');
const verificationCodeSchema = z.string().trim().regex(/^\d{6}$/, 'Enter a 6-digit code');

function diffFields(form: EditProfileValues, initial: EditProfileValues): Partial<EditProfileValues> {
  const out: Partial<EditProfileValues> = {};
  if (form.firstName !== initial.firstName) out.firstName = form.firstName;
  if (form.lastName !== initial.lastName) out.lastName = form.lastName;
  if (form.email.trim().toLowerCase() !== initial.email.trim().toLowerCase()) {
    out.email = form.email;
  }
  return out;
}

function errorCode(err: unknown): string | undefined {
  return err && typeof err === 'object' && 'code' in err
    ? (err as { code?: string }).code
    : undefined;
}

function errorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object' || !('details' in err)) return undefined;
  const details = (err as { details?: unknown }).details;
  return details && typeof details === 'object' && 'status' in details
    ? (details as { status?: number }).status
    : undefined;
}

function errorMessage(err: unknown, fallback: string): string {
  return err && typeof err === 'object' && 'userMessage' in err
    ? ((err as { userMessage?: string }).userMessage ?? fallback)
    : fallback;
}

function validateFirstName(value: string): string | undefined {
  const result = firstNameSchema.safeParse(value);
  return result.success ? undefined : result.error.issues[0]?.message;
}

function validateEmail(value: string): string | undefined {
  const result = emailSchema.safeParse(value.trim());
  return result.success ? undefined : 'Enter a valid email address.';
}

function validateVerificationCode(value: string): string | undefined {
  const result = verificationCodeSchema.safeParse(value);
  return result.success ? undefined : result.error.issues[0]?.message;
}

export function EditProfileDialog({ open, onOpenChange }: EditProfileDialogProps) {
  const { profile, refresh: refreshProfile } = useProfile();
  const patchProfile = usePatchProfileMutation();
  const verifyProfileEmail = useVerifyProfileEmailMutation();
  const resendProfileVerification = useResendProfileVerificationMutation();

  // Form pre-fills from the DB profile (verified email). Verification is
  // ephemeral — Phase 2 lives only inside the open modal session. Closing
  // the modal mid-verify drops Phase-2 state; reopening lands back in
  // Phase 1 with the verified email pre-filled. The user never sees any
  // "pending verification" affordance outside this modal.
  const initial = useMemo<EditProfileValues>(
    () => ({
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      email: profile?.email ?? '',
    }),
    [profile?.firstName, profile?.lastName, profile?.email],
  );

  const [phase, setPhase] = useState<'form' | 'verify'>('form');
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const alertPhone = useAlertPhone();
  const [pendingEmail, setPendingEmail] = useState<string>('');
  const [fieldError, setFieldError] = useState<ServerFieldError | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const initialRef = useRef(initial);

  useEffect(() => {
    initialRef.current = initial;
  }, [initial]);

  const profileForm = useForm({
    defaultValues: initial,
    onSubmit: async ({ value }) => {
      await handleSave(value);
    },
  });

  const verifyForm = useForm({
    defaultValues: { code: '' },
    onSubmit: async ({ value }) => {
      await handleVerify(value.code);
    },
  });

  // Reset to a blank form (Phase 1) every time the modal opens or closes —
  // the user explicitly does NOT want stale verification state to persist
  // across modal sessions.
  /* eslint-disable react-hooks/set-state-in-effect -- modal-session reset; must run on open/close transitions, not during render */
  useEffect(() => {
    if (!open) {
      verifyForm.reset({ code: '' });
      setFieldError(null);
      return;
    }
    setPhase('form');
    profileForm.reset(initialRef.current);
    setPendingEmail('');
    verifyForm.reset({ code: '' });
    setFieldError(null);
    setResendCooldown(0);
  }, [open, profileForm, verifyForm]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Client-side resend cooldown timer (Pitfall 4 mitigation).
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => {
      setResendCooldown((n) => Math.max(0, n - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  async function handleSave(value: EditProfileValues) {
    const changed = diffFields(value, initial);
    const hasChanges = Object.keys(changed).length > 0;
    if (!hasChanges) return;
    setFieldError(null);
    try {
      const body = await patchProfile.mutateAsync(changed);
      // Refresh the DB profile so the dropdown reflects the new
      // firstName/lastName immediately (and picks up any new pendingEmail).
      await refreshProfile();
      if (body.emailPending) {
        setPendingEmail(body.pendingEmail || value.email);
        setPhase('verify');
        verifyForm.reset({ code: '' });
        setResendCooldown(60);
        toast.success(`Check ${maskEmail(body.pendingEmail || value.email)} for a 6-digit code`);
      } else {
        toast.success('Profile updated');
        onOpenChange(false);
      }
    } catch (err) {
      if (errorStatus(err) === 409 && errorCode(err) === 'EMAIL_IN_USE') {
        setFieldError({ field: 'email', message: 'That email is already in use.' });
        return;
      }
      toast.error(errorMessage(err, 'Failed to update profile'));
    }
  }

  async function handleVerify(code: string) {
    const parsedCode = verificationCodeSchema.safeParse(code);
    if (!parsedCode.success) return;
    setFieldError(null);
    try {
      await verifyProfileEmail.mutateAsync({ code: parsedCode.data });
      // Backend swapped DB email = pendingEmail and cleared pendingEmail.
      // Refresh so the dropdown's `email` updates from old → new.
      await refreshProfile();
      toast.success('Email verified');
      onOpenChange(false);
    } catch (err) {
      if (errorStatus(err) === 400 && errorCode(err) === 'INVALID_VERIFICATION_CODE') {
        setFieldError({ field: 'code', message: 'Invalid or expired code' });
        return;
      }
      const detail = errorMessage(err, 'Network error');
      logger.warn('[verify-email] unexpected response', {
        status: errorStatus(err),
        code: errorCode(err),
      });
      setFieldError({ field: 'code', message: detail });
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || resendProfileVerification.isPending) return;
    try {
      await resendProfileVerification.mutateAsync();
      toast.success('Code resent');
      setResendCooldown(60);
    } catch (err) {
      if (errorStatus(err) === 429) {
        // Server rate-limited us — back off with the same cooldown so the
        // button stays disabled instead of letting the user hammer it.
        setResendCooldown(60);
        toast.error('Please wait a moment before resending.');
        return;
      }
      toast.error(errorMessage(err, 'Failed to resend code'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {phase === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>{TITLE_FORM}</DialogTitle>
            </DialogHeader>
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void profileForm.handleSubmit();
              }}
              className="space-y-3"
            >
              <profileForm.Field
                name="firstName"
                validators={{
                  onChange: ({ value }) => validateFirstName(value),
                  onSubmit: ({ value }) => validateFirstName(value),
                }}
              >
                {(field) => {
                  const hasError = field.state.meta.errors.length > 0;
                  return (
                    <div className="space-y-2">
                      <Label htmlFor="edit-profile-first-name">First name</Label>
                      <Input
                        id="edit-profile-first-name"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        maxLength={100}
                        aria-invalid={hasError || undefined}
                        aria-describedby={hasError ? 'edit-profile-first-name-error' : undefined}
                        autoFocus
                      />
                      <FieldError
                        id="edit-profile-first-name-error"
                        errors={field.state.meta.errors}
                      />
                    </div>
                  );
                }}
              </profileForm.Field>
              <profileForm.Field name="lastName">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="edit-profile-last-name">Last name</Label>
                    <Input
                      id="edit-profile-last-name"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      maxLength={100}
                    />
                  </div>
                )}
              </profileForm.Field>
              <profileForm.Field
                name="email"
                validators={{
                  onChange: ({ value }) => validateEmail(value),
                  onSubmit: ({ value }) => validateEmail(value),
                }}
              >
                {(field) => {
                  const hasServerError = fieldError?.field === 'email';
                  const hasError = hasServerError || field.state.meta.errors.length > 0;
                  return (
                    <div className="space-y-2">
                      <Label htmlFor="edit-profile-email">Email</Label>
                      <EmailInput
                        id="edit-profile-email"
                        value={field.state.value}
                        onChange={(e) => {
                          if (fieldError?.field === 'email') setFieldError(null);
                          field.handleChange(e.target.value);
                        }}
                        onBlur={field.handleBlur}
                        maxLength={254}
                        aria-invalid={hasError || undefined}
                        aria-describedby={hasError ? 'edit-profile-email-error' : undefined}
                      />
                      {hasServerError ? (
                        <FieldError
                          id="edit-profile-email-error"
                          errors={[fieldError.message]}
                        />
                      ) : (
                        <FieldError
                          id="edit-profile-email-error"
                          errors={field.state.meta.errors}
                        />
                      )}
                    </div>
                  );
                }}
              </profileForm.Field>
              <div className="space-y-1">
                <Label>Alert phone</Label>
                <div className="flex items-center justify-between gap-2">
                  {alertPhone.loading ? (
                    <span className="text-sm text-muted-foreground">Loading…</span>
                  ) : alertPhone.status?.phone ? (
                    <>
                      <span className="text-sm">{alertPhone.status.phone}</span>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setPhoneDialogOpen(true)}>
                          Change
                        </Button>
                        <RemoveAlertPhoneButton onRemoved={() => void alertPhone.refresh()} />
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-muted-foreground">Get alerted when things break.</span>
                      <Button type="button" variant="outline" size="sm" onClick={() => setPhoneDialogOpen(true)}>
                        Add phone
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <TwoFactorSetting />
              <profileForm.Subscribe selector={(state) => state.values}>
                {(values) => {
                  const changed = diffFields(values, initial);
                  const hasChanges = Object.keys(changed).length > 0;
                  // Footer only appears once a text field is dirty — alert
                  // phone actions apply instantly and must not look undoable.
                  if (!hasChanges) return null;
                  const formInvalid =
                    !!validateFirstName(values.firstName) || !!validateEmail(values.email);
                  return (
                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => profileForm.reset(initialRef.current)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={patchProfile.isPending || formInvalid}>
                        {patchProfile.isPending && <Loader2 className="size-3.5 animate-spin" />}
                        Save
                      </Button>
                    </div>
                  );
                }}
              </profileForm.Subscribe>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{TITLE_VERIFY}</DialogTitle>
              <DialogDescription>
                Enter the 6-digit code we sent to {maskEmail(pendingEmail)}
              </DialogDescription>
            </DialogHeader>
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void verifyForm.handleSubmit();
              }}
              className="space-y-3"
            >
              <verifyForm.Field
                name="code"
                validators={{
                  onChange: ({ value }) => validateVerificationCode(value),
                  onSubmit: ({ value }) => validateVerificationCode(value),
                }}
              >
                {(field) => {
                  const hasServerError = fieldError?.field === 'code';
                  const hasError = hasServerError || field.state.meta.errors.length > 0;
                  return (
                    <div className="space-y-2">
                      <Label htmlFor="edit-profile-code">Verification code</Label>
                      <Input
                        id="edit-profile-code"
                        type="text"
                        inputMode="numeric"
                        maxLength={16}
                        value={field.state.value}
                        onChange={(e) => {
                          if (fieldError?.field === 'code') setFieldError(null);
                          field.handleChange(e.target.value);
                        }}
                        onBlur={field.handleBlur}
                        aria-invalid={hasError || undefined}
                        aria-describedby={hasError ? 'edit-profile-code-error' : undefined}
                        autoFocus
                      />
                      {hasServerError ? (
                        <FieldError
                          id="edit-profile-code-error"
                          errors={[fieldError.message]}
                        />
                      ) : (
                        <FieldError
                          id="edit-profile-code-error"
                          errors={field.state.meta.errors}
                        />
                      )}
                    </div>
                  );
                }}
              </verifyForm.Field>
              <div className="flex items-center justify-between pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => void handleResend()}
                  disabled={resendCooldown > 0 || resendProfileVerification.isPending}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <verifyForm.Subscribe selector={(state) => state.values.code}>
                    {(code) => {
                      const codeInvalid = !!validateVerificationCode(code);
                      return (
                        <Button type="submit" disabled={verifyProfileEmail.isPending || codeInvalid}>
                          {verifyProfileEmail.isPending && <Loader2 className="size-3.5 animate-spin" />}
                          Verify
                        </Button>
                      );
                    }}
                  </verifyForm.Subscribe>
                </div>
              </div>
            </form>
          </>
        )}
      </DialogContent>
      <AlertPhoneDialog
        open={phoneDialogOpen}
        onOpenChange={setPhoneDialogOpen}
        onVerified={() => void alertPhone.refresh()}
        current={alertPhone.status}
      />
    </Dialog>
  );
}
